import {Injectable} from '@angular/core';
import {Observable} from "rxjs/Observable";
import {GenericResourceApiService} from "@catfeatures/services/generic-resource-api.service";
import {Subscription} from "rxjs/Subscription";
import {EntityTypes, EntityTypeStrings, ItemFieldNames} from "@catmodels/linked-entities/types";
import {Router} from "@angular/router";
import {Restangular} from "ngx-restangular";
import {isNullOrUndefined} from "util";

@Injectable()
export class LinkedEntitiesService {

  private subresourceSubscriptions: Subscription[] = [];
  private linkedEntitesOptionsAction = '/linked-entities-options';

  constructor(private resourceService: GenericResourceApiService,
              private restService: Restangular) {
    this.subresourceSubscriptions.forEach((s) => s.unsubscribe());
    this.subresourceSubscriptions = [];
  }

  /**
   * Fetching multiple resources with a forked join and returns a Observable
   *
   * @param {Array<{href: string}>} resources
   * @returns {Observable<any[]>}
   */
  private retrieveResourcesFor(resources: Array<{ href: string }> ) {
    const callJoins = [];

    resources.forEach((resource) => callJoins.push(
      this.resourceService
        .getServiceForResource(resource.href)
        .get()),
    );

    this.subresourceSubscriptions = [...this.subresourceSubscriptions, ...callJoins];
    return Observable.forkJoin(callJoins);
  }

  /**
   * This function will try to build a list of linked entities.
   *
   * @param linkedEntityResources
   * @param entityType
   * @param navigateDestination Navigation function that will be called with port as parameter
   * @returns {any[]}
   */
  buildLinkedEntityList(linkedEntityResources: Array<{href: string}>, entityType, navigateDestination: any = () => {}) {
    const res = [];
    this.retrieveResourcesFor(linkedEntityResources)
      .subscribe((resources: any[]) => {

        resources.forEach((linkedEntity, index: number) => {
          const item = {
            label: linkedEntity['key'],
            raw: linkedEntity,
            children: [],
            resourceUri: linkedEntity._links.self.href,
            addDisabled: false,
            deleteDisabled: false,
            onlyOneEntity: linkedEntity.only_one_entity,
          };

          const itemFieldName = ItemFieldNames[entityType];
          if (!isNullOrUndefined(linkedEntity._links[itemFieldName])) {
            linkedEntity._links[itemFieldName].forEach((linkedItem) => {
              item.children.push({
                label: linkedItem.label,
                navigate: (router: Router) => navigateDestination(linkedItem),
                resourceUri: linkedItem.href,
                raw: null,
              });
            });
          }

          item.addDisabled = linkedEntity.only_one_entity && item.children.length >= 1;
          item.deleteDisabled = item.children.length === 0;

          res.push(item);
        });
      });

    return res;
  }

  /**
   * Request the selectable entities for the linked entities entity based on feature or project level
   *
   * @param linkedEntitiesRef
   * @param entityType
   * @param featureLevel
   */
  getSelectableEntities(linkedEntitiesRef: string = null, entityType: number = null, featureLevel: boolean = true) {
    if (isNullOrUndefined(linkedEntitiesRef) || isNullOrUndefined(entityType)) {
      return [];
    }
    return this.restService.all(this.linkedEntitesOptionsAction).customGET('', {
      uri: linkedEntitiesRef,
      type: EntityTypeStrings[entityType],
      feature: featureLevel,
    });
  }

  setEntities(linkedEntitiesRef: string, entities, entityType) {
    const entityKey = this.getEntityKey(entityType);
    const data = {};
    data[entityKey] = entities;
    return this.restService.one(this.clearEndpoint(linkedEntitiesRef)).customPUT(data);
  }

  getEntityKey(entityType: number) {
    switch (entityType) {
      case EntityTypes.Ports:
        return 'ports';
      case EntityTypes.Units:
        return 'units';
      case EntityTypes.Vids:
        return 'vids';
      case EntityTypes.Services:
        return 'services';
      case EntityTypes.GuiZones:
        return 'gui_zones';
    }
  }

  /**
   * cleaning up the endpoint, because our api provides us with there subdirectory "apipf", which is already defined as base entrypoint
   * for Restangular.
   *
   * @param {string} endpoint
   * @returns {string}
   */
  private clearEndpoint(endpoint: string): string {
    return endpoint.replace(/^(\/api)/, '');
  }
}



// WEBPACK FOOTER //
// /agent/workspace/CUS-LHT_NICE_nto_nto_CAT_release@2/app/Resources/Public/src/app/modules/models/linked-entities/services/linked-entities.service.ts